import React from "react";
import PropTypes from "prop-types";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { exportHierarchyToExcel } from "./ExportUtils";
import { Button } from "reactstrap";
import { OrgChartNode } from "./GroupStore";
// Recursive function to render each node.
const renderTree = (node: OrgChartNode) => (
    <TreeItem key={node.id} itemId={node.id} label={node.name}>
        {Array.isArray(node.children) && node.children.map((child) => renderTree(child))}
    </TreeItem>
);

/**
 * Recursively collects all node IDs from a group (or array of groups).
 *
 * @param {Object|Array} groups - A group object or an array of group objects.
 *                                Each group is expected to have an "id" property,
 *                                and optionally a "children" array property.
 * @returns {Array} An array of node IDs (strings or numbers).
 */
export function getAllGroupIds(groups: OrgChartNode[]) {
    const ids = [] as string[];

    // Recursive helper function to traverse each node.
    function traverse(node: OrgChartNode) {
        if (!node) return;

        // If the node has an id, add it to the result.
        if (node.id) {
            ids.push(node.id);
        }

        // If the node has children, traverse each child.
        if (node.children && Array.isArray(node.children)) {
            node.children.forEach((child) => traverse(child));
        }
    }

    // Check if groups is an array; if not, assume it's a single object.
    if (Array.isArray(groups)) {
        groups.forEach((group) => traverse(group));
    } else {
        traverse(groups);
    }

    return ids;
}

interface FolderTreeViewProps {
    groups: OrgChartNode[];
}

const FolderTreeView = ({ groups }: FolderTreeViewProps) => {
    const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

    const handleExpandedItemsChange = (event: React.SyntheticEvent, itemIds: string[]) => {
        setExpandedItems(itemIds);
    };

    const handleExpandClick = () => {
        setExpandedItems((oldExpanded) => (oldExpanded.length === 0 ? getAllGroupIds(groups) : []));
    };

    // Ensure that groups is always treated as an array.
    const treeData = Array.isArray(groups) ? groups : [groups];

    return (
        <div>
            <Button
                outline={true}
                color="primary"
                onClick={() => {
                    exportHierarchyToExcel(groups);
                }}
            >
                Export
            </Button>
            <Button onClick={handleExpandClick}>{expandedItems.length === 0 ? "Expand all" : "Collapse all"}</Button>
            <SimpleTreeView expandedItems={expandedItems} onExpandedItemsChange={handleExpandedItemsChange}>
                {treeData.map((node) => renderTree(node))}
            </SimpleTreeView>
        </div>
    );
};

FolderTreeView.propTypes = {
    // groups can be a single tree (object) or an array of trees.
    groups: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default FolderTreeView;
